import {Component, EventEmitter, inject, input, InputSignal, Output, AfterViewInit, OnDestroy, ElementRef, ViewChild, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {PaymentProvider, PaymentTransactionModel} from './paymentTransaction.model';
import {PaymentProviderService} from "../../services/payment-provider.service";
import {ConfigService} from "../../services/config.service";
import {RedirectService} from "../../../data/services/redirect.service";
import {WebSocketService} from "../../services/web-socket.service";
import {TranslationService} from "../../services/translation.service";
import {ApplyIfNotEmptyDirective} from "../../directives/apply-if-not-empty.directive";
import {Address} from "../../../data/models/address.model";
import {OrderProcessingService} from "../../../data/services/order-processing.service";
import {AddressFormValue} from "../../../data/models/subscription-order.model";
import {mergeExternalAddress} from "../../utils/external-address-merge";
import {BaseDataService} from "../../services/base-data.service";
import {AddressCode} from "../../../data/models/address-code.model";
import {firstValueFrom} from "rxjs";
import {
  CreateOrderActions, CreateOrderData,
  CreateSubscriptionActions,
  loadScript,
  OnApproveActions,
  OnApproveData,
  PayPalNamespace,
  PayPalScriptOptions, SubscriptionResponseBody
} from "@paypal/paypal-js";
import {components} from "@paypal/paypal-js/types/apis/openapi/checkout_orders_v2";

@Component({
  selector: 'app-onlinepayment',
  templateUrl: './onlinepayment.component.html',
  styleUrls: ['./onlinepayment.component.scss'],
  imports: [
    ApplyIfNotEmptyDirective
  ],
  standalone: true
})
export class OnlinepaymentComponent implements AfterViewInit, OnDestroy {
  private readonly validFundingSources = ['card', 'credit', 'paylater', 'bancontact', 'blik', 'eps', 'giropay', 'ideal', 'mercadopago', 'mybank', 'p24', 'sepa', 'sofort', 'venmo'];

  private hupSubscriptionsService = inject(HupSubscriptionsService);
  private paymentTransactionService = inject(PaymentProviderService);
  private configService = inject(ConfigService);
  private baseDataService = inject(BaseDataService);
  private redirectService = inject(RedirectService);
  private wssService = inject(WebSocketService);
  translationService = inject(TranslationService);
  private orderProcessingService = inject(OrderProcessingService);
  private platformId = inject(PLATFORM_ID);
  private paypal: PayPalNamespace;

  subscriptionStartTime: InputSignal<Date> = input();

  @Output() onlinePaymentSuccess = new EventEmitter<boolean>();

  @ViewChild('paypalButtonContainer') paypalButtonContainer: ElementRef;

  buckarooTransactionId: string = null;
  buckarooProcessingSuccessful: boolean = true;
  isBuckaroo = false;
  isPaypal = false;
  transaction: PaymentTransactionModel = null;

  payPalClientId: string = null;
  payPalIsoCurrencyCode: string = null;
  payPalMerchantId: string = null;

  private pendingTransaction: PaymentTransactionModel = null;
  private isSubscription = false;

  constructor() {
    this.payPalClientId = this.configService.getConfig('paymentprovider.paypal.clientid')?.value;
    this.payPalIsoCurrencyCode = this.configService.getConfig('paymentprovider.paypal.isoCurrency')?.value;
    this.payPalMerchantId = this.configService.getConfig('paymentprovider.paypal.merchantid')?.value;

    console.log('OnlinepaymentComponent - PayPal Config:', {
      clientId: this.payPalClientId,
      currency: this.payPalIsoCurrencyCode,
      merchantId: this.payPalMerchantId
    });

    this.paymentTransactionService.getPaymentProviderResultData()?.subscribe(transaction => {
      const transactionForOrder = transaction && parseFloat(transaction.amount) > 0 && transaction.invoiceText &&
        (transaction.transactionId === '' || !transaction.transactionId);

      const transactionForSubscription = transaction && transaction.billingPlanId?.length > 0 &&
        (!transaction.subscriptionId || transaction.subscriptionId === '');

      if (transactionForOrder || transactionForSubscription) {
        if (transaction.provider === PaymentProvider.BUCKAROO) {
          this.initBuckaroo(transaction);
        } else if (transaction.provider === PaymentProvider.PAYPAL) {
          this.isPaypal = true;
          this.isSubscription = transactionForSubscription;
          this.pendingTransaction = transaction;

          if (isPlatformBrowser(this.platformId)) {
            this.loadPayPalScript(transactionForSubscription);
          }
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // Buttons werden nach Script-Load gerendert
  }

  ngOnDestroy(): void {

  }

  private initBuckaroo(transaction: PaymentTransactionModel): void {
    this.isBuckaroo = true;
    transaction.wssId = this.wssService.getWssId();
    this.hupSubscriptionsService.startBuckarooPaymentTransaction(transaction).subscribe(result => {
      if (result && result.transactionId) {
        this.buckarooProcessingSuccessful = true;
        this.buckarooTransactionId = result.transactionId;
        transaction.redirectUrl = result.redirectUrl;
        transaction.transactionId = result.transactionId;
        this.transaction = transaction;
      }
    });
  }

  private loadPayPalScript(isSubscription: boolean, withGooglePay: boolean = true) {
    // Google Pay Library laden
    if (withGooglePay && !document.getElementById('google-pay-script')) {
      const gpayScript = document.createElement('script');
      gpayScript.id = 'google-pay-script';
      gpayScript.src = 'https://pay.google.com/gp/p/js/pay.js';
      gpayScript.async = true;
      document.body.appendChild(gpayScript);
    }

    const disabledFundingSources = this.getDisabledFundingSources();
    let config: PayPalScriptOptions = {
      clientId: this.payPalClientId,
      currency: this.payPalIsoCurrencyCode,
      merchantId: this.payPalMerchantId,
      intent: isSubscription ? "subscription" : "authorize",
      vault: isSubscription,
      commit: !isSubscription,
      components: withGooglePay ? ["buttons", "googlepay"] : [],
      disableFunding: disabledFundingSources.length === 0 ? undefined : disabledFundingSources
    }

    loadScript(config).then(paypal=> {
      this.paypal = paypal;
      this.renderPayPalButtons();
    });
  }

  getDisabledFundingSources(): string[]{
    return this.configService.getConfig('paymentprovider.paypal.disable-funding')
        ?.value
        ?.split(',')
        ?.map(source=>source.trim())
        ?.filter(source=>{
          const allowed = this.validFundingSources.includes(source);
          if(!allowed){
            console.error(`invalid funding source '${source}' in config property 'paymentprovider.paypal.disable-funding'`);
          }
          return allowed;
        }) ?? [];
  }

  private renderPayPalButtons(): void {
    // Container leeren
    this.paypalButtonContainer.nativeElement.innerHTML = '';

    const transaction = this.pendingTransaction;

    this.paypal.Buttons({
      style: {
        layout: 'vertical',
        label: 'paypal',
        height: 40
      },
      createOrder: this.isSubscription ? undefined : async (data: CreateOrderData, actions: CreateOrderActions) => {
        const orderPayload = {
          intent: 'AUTHORIZE',
          purchase_units: [{
            amount: {
              currency_code: this.payPalIsoCurrencyCode,
              value: transaction.amount
            },
            description: transaction.invoiceText
          }]
        };
        console.log('PayPal Buttons - createOrder Payload:', orderPayload);
        const response = await firstValueFrom(this.hupSubscriptionsService.createPayPalOrder(orderPayload));
        if (!response?.id) {
          throw new Error('PayPal order id missing in createOrder response');
        }
        return response.id;
      },
      createSubscription: !this.isSubscription ? undefined : (data: Record<string, unknown>, actions: CreateSubscriptionActions) => {
        try {
          const createPayload: any = {
            plan_id: transaction.billingPlanId,
            // CONTINUE: PayPal aktiviert das Abo nach Zustimmung NICHT selbst (Status APPROVED, keine Abbuchung).
            // Die Aktivierung muss erst nach erfolgreichem Bestellabschluss erfolgen.
            application_context: {
              user_action: 'CONTINUE'
            }
          };
          const startTime = this.toPayPalStartTime(this.subscriptionStartTime?.());
          if (startTime) {
            createPayload.start_time = startTime;
          }
          console.log('PayPal Subscription - create payload:', createPayload);
          return actions.subscription.create(createPayload);
        } catch (error) {
          console.error('Subscription create failed before request:', error);
          this.onlinePaymentSuccess.emit(false);
          return Promise.reject(error);
        }
      },
      onApprove: this.isSubscription ? this.approveSubscription.bind(this) : this.approveOrder.bind(this),
      onCancel: (data: Record<string, unknown>) => {
        console.log('Subscription cancelled', data);
      },
      onError: (err: Record<string, unknown>) => {
        console.error('Subscription error', err);
        this.onlinePaymentSuccess.emit(false);
      }
    })
        .render(this.paypalButtonContainer.nativeElement)

        // Google Pay Button separat rendern (wenn verfügbar)
        .then(()=>this.renderGooglePayButton(transaction));
  }

  private async approveSubscription(data: OnApproveData, actions: OnApproveActions) {
    const transaction = this.pendingTransaction;
    console.log('Subscription approved:', data);

    const details = await actions.subscription.get() as SubscriptionResponseBody & {
      subscriber: { // leider ist der type nicht vollständig, deshalb hier ein paar ergänzungen
        shipping_address: {address: components["schemas"]["address_portable"]},
        email_address: string
      },
      billing_info: {
        cycle_executions: {
          tenure_type: "REGULAR" | "TRIAL",
          total_price_per_cycle: { gross_amount: { value: string } }
        }[]
      }
    }
    console.log('Subscription details:', details);

    transaction.subscriptionId = data.subscriptionID;
    transaction.clientAddress = this.mapUsersAddress(details.subscriber?.shipping_address?.address);
    transaction.amount = details.billing_info?.cycle_executions
        ?.filter(ce=>ce.tenure_type==="REGULAR")
        [0]
        ?.total_price_per_cycle?.gross_amount?.value
        // Bei Status APPROVED (user_action CONTINUE) liefert PayPal noch kein billing_info
        ?? transaction.amount;

    const paypalAddress = this.buildPayPalAddressFormValue(transaction.clientAddress, details, true);
    const email = details.subscriber?.email_address;

    this.paymentTransactionService.setNewTransaction({
      ...transaction
    });

    this.orderProcessingService.update(state => ({
      ...state,
      transaction: transaction,
      addresses: {
        ...state.addresses,
        email: (state.addresses.email && state.addresses.email !== '')
            ? state.addresses.email
            : email,
        // Adresse aus PayPal nur bei digitalen Abos und nur, wenn der Nutzer selbst keine hat.
        deliveryAddress: mergeExternalAddress(state.addresses?.deliveryAddress, paypalAddress, state.offer),
        billingAddress: mergeExternalAddress(state.addresses?.billingAddress, paypalAddress, state.offer)
      }
    }));

    this.orderProcessingService.update(state => ({
      ...state,
      transaction: transaction
    }));

    this.onlinePaymentSuccess.emit(transaction.subscriptionId != null);
  }

  private async approveOrder(data: OnApproveData, actions: OnApproveActions){
    const transaction = this.pendingTransaction;
    console.debug('PayPal approved, authorizing...');

    const orderId = data?.orderID;
    if (!orderId) {
      console.error('PayPal onApprove without orderID', data);
      this.onlinePaymentSuccess.emit(false);
      return;
    }

    const details = await firstValueFrom(this.hupSubscriptionsService.authorizePayPalOrder(orderId))
    console.log('onApprove - authorization details:', details);

    const authorizationId = details?.purchase_units?.[0]?.payments?.authorizations?.[0]?.id;

    if (!authorizationId) {
      console.debug('No authorizationId', details);
      return;
    }

    transaction.authorizationId = authorizationId;
    transaction.transactionId = authorizationId;
    transaction.status = 'AUTHORIZED';

    const shippingAddress = details.purchase_units?.[0]?.shipping?.address;
    const person = details.purchase_units?.[0]?.shipping?.name;

    console.log('Zahler:', person);

    transaction.clientAddress = this.mapUsersAddress(shippingAddress);
    const paypalAddress = this.buildPayPalAddressFormValue(transaction.clientAddress, details, false);
    const email = details.payment_source?.paypal?.email_address;

    this.paymentTransactionService.setNewTransaction({
      ...transaction
    });

    this.orderProcessingService.update(state => ({
      ...state,
      transaction: transaction,
      addresses: {
        ...state.addresses,
        email: (state.addresses.email && state.addresses.email !== '')
            ? state.addresses.email
            : email,
        // Adresse aus PayPal nur bei digitalen Abos und nur, wenn der Nutzer selbst keine hat.
        deliveryAddress: mergeExternalAddress(state.addresses?.deliveryAddress, paypalAddress, state.offer),
        billingAddress: mergeExternalAddress(state.addresses?.billingAddress, paypalAddress, state.offer)
      },
    }));

    this.onlinePaymentSuccess.emit(!!transaction.authorizationId);
  }

  private async renderGooglePayButton(transaction: PaymentTransactionModel): Promise<void> {
    // Prüfen ob wir in einem secure context sind
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      console.log('Google Pay erfordert HTTPS.');
      return;
    }

    // Prüfen ob Google Pay Komponente verfügbar ist
    if (!this.paypal["Googlepay"]) {
      console.log('Google Pay Komponente nicht im SDK verfügbar');
      return;
    }

    // Prüfen ob Google Pay API geladen ist
    if (!(window as any).google?.payments?.api?.PaymentsClient) {
      console.log('Google Pay API (pay.js) noch nicht geladen, warte...');
      setTimeout(() => this.renderGooglePayButton(transaction), 500);
      return;
    }

    try {
      const googlepay = this.paypal["Googlepay"]();
      const googlepayConfig = await googlepay.config();

      console.log('Google Pay Config:', googlepayConfig);

      // Google Pay Button Container erstellen
      const gpayContainer = document.createElement('div');
      gpayContainer.id = 'googlepay-button-container';
      gpayContainer.style.marginBottom = '10px';
      this.paypalButtonContainer.nativeElement.prepend(gpayContainer);

      // Google Payments Client initialisieren
      const paymentsClient = new (window as any).google.payments.api.PaymentsClient({
        environment: googlepayConfig.environment || 'TEST',
        paymentDataCallbacks: {
          onPaymentAuthorized: (paymentData: any) => {
            return new Promise((resolve) => {
              this.onGooglePayAuthorized(googlepay, paymentData, transaction, googlepayConfig)
                .then(() => resolve({ transactionState: 'SUCCESS' }))
                .catch((error) => {
                  console.error('Google Pay Authorization Error:', error);
                  resolve({
                    transactionState: 'ERROR',
                    error: {
                      intent: 'PAYMENT_AUTHORIZATION',
                      message: error.message || 'Zahlung fehlgeschlagen',
                      reason: 'OTHER_ERROR'
                    }
                  });
                });
            });
          }
        }
      });

      // Prüfen ob Google Pay verfügbar ist
      const isReadyToPayRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: googlepayConfig.allowedPaymentMethods
      };

      const isReadyToPay = await paymentsClient.isReadyToPay(isReadyToPayRequest);

      if (!isReadyToPay.result) {
        console.log('Google Pay ist auf diesem Gerät/Browser nicht verfügbar');
        return;
      }

      // Google Pay Button erstellen
      const button = paymentsClient.createButton({
        onClick: () => { },
        buttonColor: 'black',
        buttonType: 'pay',
        buttonSizeMode: 'fill'
      });

      const paymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: googlepayConfig.allowedPaymentMethods,
        emailRequired: true,
        shippingAddressRequired: true,
        shippingAddressParameters: {
          phoneNumberRequired: true
        },
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPrice: parseFloat(transaction.amount + '').toFixed(2),
          currencyCode: this.payPalIsoCurrencyCode,
          countryCode: 'DE'
        },
        merchantInfo: googlepayConfig.merchantInfo,
        callbackIntents: ['PAYMENT_AUTHORIZATION']
      };

      button.addEventListener('click', () => {
        paymentsClient.loadPaymentData(paymentDataRequest)
          .catch(err => {
            if (err.statusCode === 'CANCELED') {
              console.log('Google Pay vom Nutzer abgebrochen');
            } else {
              console.error('Google Pay loadPaymentData Fehler:', err);
            }
          });
      });

      button.style.width = '100%';
      button.style.height = '40px';

      gpayContainer.appendChild(button);
      console.log('Google Pay Button gerendert');

    } catch (error: any) {
      console.log('Google Pay Initialisierung fehlgeschlagen:', error);
    }
  }

  private async onGooglePayAuthorized(
    googlepay: any,
    paymentData: any,
    transaction: PaymentTransactionModel,
    googlepayConfig: any
  ): Promise<void> {
    try {
      console.log('googlepay SDK members (all):', Object.getOwnPropertyNames(Object.getPrototypeOf(googlepay)));

      // Wir bauen den Payload laut PayPal Google Pay API Spezifikation für Single-Step.
      // Der 'intent' darf NICHT im Payload stehen, wenn er bereits in der SDK-URL steht.
      const orderPayload = {
        intent: 'AUTHORIZE',
        purchase_units: [{
          amount: {
            currency_code: this.payPalIsoCurrencyCode,
            value: parseFloat(transaction.amount + '').toFixed(2)
          },
          description: transaction.invoiceText
        }]
      };

      const merchantIdFromPayPal = googlepayConfig?.merchantInfo?.merchantId;
      console.log('Google Pay - Sende confirmOrder (Single-Step)...', {
        merchantId: merchantIdFromPayPal,
        orderPayload
      });

      const createdOrder = await firstValueFrom(this.hupSubscriptionsService.createPayPalOrder(orderPayload));
      const orderId = createdOrder?.id;
      if (!orderId) {
        throw new Error('PayPal order id missing for Google Pay confirmOrder');
      }

      const orderResponse = await googlepay.confirmOrder({
        orderId: orderId,
        paymentMethodData: paymentData.paymentMethodData
      });

      console.log('Google Pay - PayPal Response:', orderResponse);

      if (orderResponse.status === 'APPROVED' || orderResponse.status === 'COMPLETED') {
        transaction.transactionId = orderResponse.id;
        transaction.status = 'COMPLETED';

        const shippingAddress = paymentData.shippingAddress;
        if (shippingAddress) {
          transaction.clientAddress = new Address(
            shippingAddress.address1 || '',
            shippingAddress.address2 || '',
            shippingAddress.postalCode || '',
            shippingAddress.locality || '',
            shippingAddress.countryCode || '',
            null, null, null, null, null, null, null, null
          );
        }

        const googlePayAddress = this.buildGooglePayAddressFormValue(paymentData, orderResponse);

        this.paymentTransactionService.setNewTransaction({ ...transaction });
        this.orderProcessingService.update(state => ({
          ...state,
          transaction,
          addresses: {
            ...state.addresses,
            // Adresse aus Google Pay nur bei digitalen Abos und nur, wenn der Nutzer selbst keine hat.
            deliveryAddress: mergeExternalAddress(state.addresses?.deliveryAddress, googlePayAddress, state.offer),
            billingAddress: mergeExternalAddress(state.addresses?.billingAddress, googlePayAddress, state.offer)
          }
        }));
        this.onlinePaymentSuccess.emit(true);
      } else {
        throw new Error(`PayPal Status: ${orderResponse.status}`);
      }
    } catch (error) {
      throw error;
    }
  }

  mapUsersAddress(subscribersAddress: components["schemas"]["address_portable"]): Address {
    if (!subscribersAddress) {
      return null;
    }

    const streetAndHouseNoArray: string[] = subscribersAddress.address_line_1?.split(" ") || [];

    const houseNo: string = streetAndHouseNoArray.pop() || '';
    const street: string = streetAndHouseNoArray.join(" ");
    const city: string = subscribersAddress.admin_area_2;
    const zipcode: string = subscribersAddress.postal_code;
    const countryCode: string = subscribersAddress.country_code;
    const additionalInfo: string = subscribersAddress.address_line_2;
    return new Address(
      street, houseNo, zipcode, city, countryCode, null, null, null, null, null, null, null, null
    );
  }

  private buildGooglePayAddressFormValue(paymentData: any, orderResponse?: any): AddressFormValue | null {
    const shippingAddress = paymentData?.shippingAddress ?? orderResponse?.purchase_units?.[0]?.shipping?.address;
    if (!shippingAddress) {
      return null;
    }

    const shippingName = paymentData?.shippingAddress?.name ?? orderResponse?.purchase_units?.[0]?.shipping?.name?.full_name ?? '';
    const payerGivenName = orderResponse?.payer?.name?.given_name ?? '';
    const payerSurname = orderResponse?.payer?.name?.surname ?? '';
    const fullName = shippingName || `${payerGivenName} ${payerSurname}`.trim();
    const firstName = fullName.split(' ')[0] ?? '';
    const lastName = fullName.split(' ').slice(1).join(' ') ?? '';

    const countries = this.baseDataService.getBaseData('countrycodes');
    let countryCodeParadise = null;
    countries.forEach(value => {
      if (value.value == (shippingAddress.countryCode ?? shippingAddress.country_code)) {
        countryCodeParadise = value.key;
      }
    });

    const salutation = this.configService.getConfig('paypal.defaultAddressCode');
    let addressCode = null;
    if (salutation) {
      const addressCodes = this.baseDataService.getBaseData('addresscodes');
      addressCodes.forEach(value => {
        if (value.key == salutation.value) {
          addressCode = (value as AddressCode)
        }
      })
    }

    const shippingAddressLine1 = shippingAddress.address1 ?? shippingAddress.address_line_1 ?? '';
    const shippingAddressLine2 = shippingAddress.address2 ?? shippingAddress.address_line_2 ?? '';
    const shippingPostalCode = shippingAddress.postalCode ?? shippingAddress.postal_code ?? '';
    const shippingCity = shippingAddress.locality ?? shippingAddress.admin_area_2 ?? '';

    const streetAndHouseNoArray: string[] = shippingAddressLine1.split(" ");
    const houseNo: string = streetAndHouseNoArray.pop() || '';
    const street: string = streetAndHouseNoArray.join(" ");

    const loggedInEmail = this.getKnownUserEmail();
    const normalizedGooglePhone = this.normalizeExternalPhone(
      paymentData?.shippingAddress?.phoneNumber ?? '',
      ''
    );

    return {
      showAddress: true,
      person: {
        salutation: addressCode ?? '',
        title: '',
        firstName: firstName,
        lastName: lastName,
        firstname: firstName,
        lastname: lastName,
        initials: '',
        middlename: ''
      },
      userData: {
        email: loggedInEmail,
        phonecode: normalizedGooglePhone.phonecode,
        phone: normalizedGooglePhone.phone
      },
      address: {
        street: street ?? '',
        houseNo: houseNo ?? '',
        houseno: houseNo ?? '',
        zipcode: shippingPostalCode ?? '',
        city: shippingCity ?? '',
        country: countryCodeParadise ?? '',
        extraLine: shippingAddressLine2 ?? '',
        district: ''
      }
    } as AddressFormValue;
  }

  private getKnownUserEmail(): string {
    const state = this.orderProcessingService.snapshot();
    return state?.addresses?.deliveryAddress?.userData?.email
      ?? state?.addresses?.billingAddress?.userData?.email
      ?? state?.registration?.email
      ?? state?.addresses?.email
      ?? state?.userData?.emailList?.[0]
      ?? '';
  }

  private getKnownUserPhone(): { phonecode: string; phone: string } {
    const state = this.orderProcessingService.snapshot();
    const deliveryUserData = state?.addresses?.deliveryAddress?.userData;
    const billingUserData = state?.addresses?.billingAddress?.userData;

    const fallbackPhoneCode = deliveryUserData?.phonecode
      ?? billingUserData?.phonecode
      ?? '';
    const fallbackPhone = deliveryUserData?.phone
      ?? billingUserData?.phone
      ?? '';

    return this.normalizeExternalPhone(fallbackPhone, fallbackPhoneCode);
  }

  private normalizeExternalPhone(rawPhone: string, rawPhoneCode?: string): { phonecode: string; phone: string } {
    const phoneCode = (rawPhoneCode ?? '').toString().trim();
    let phone = (rawPhone ?? '').toString().trim();

    if (phoneCode && phone.startsWith(phoneCode)) {
      phone = phone.slice(phoneCode.length).trim();
    }

    phone = phone.replace(/^[\s/-]+/, '').trim();
    return {
      phonecode: phoneCode,
      phone: phone
    };
  }

  private buildPayPalAddressFormValue(paypalAddress: Address, details: any, isSubscription: boolean): AddressFormValue | null {
    if (!paypalAddress) {
      return null;
    }

    let firstName = '';
    let lastName = '';
    if (isSubscription) {
      firstName = details?.subscriber?.name?.given_name;
      lastName = details?.subscriber?.name?.surname;
    }else {
      const payer = details?.payer?.name;
      const shippingFullName = details?.purchase_units?.[0]?.shipping?.name?.full_name ?? '';
      firstName = payer?.given_name ?? (shippingFullName.split(' ')[0] ?? '');
      lastName = payer?.surname ?? (shippingFullName.split(' ').slice(1).join(' ') ?? '');
    }

    // E-Mail im Bestellprozess immer aus dem bestehenden State übernehmen (Schritt 1/Login),
    // nicht aus PayPal, damit die eingegebene Adresse konsistent bleibt.
    const email = this.getKnownUserEmail();

    const fallbackUserPhone = this.getKnownUserPhone();
    const paypalPhoneRaw = details?.payer?.phone?.phone_number?.national_number
      ?? details?.subscriber?.phone?.phone_number?.national_number
      ?? details?.payer?.phone?.phone_number
      ?? details?.subscriber?.phone?.phone_number
      ?? details?.purchase_units?.[0]?.shipping?.phone_number?.national_number
      ?? details?.purchase_units?.[0]?.shipping?.phone_number
      ?? fallbackUserPhone.phone
      ?? '';

    const paypalPhoneCodeRaw = details?.payer?.phone?.phone_number?.country_code
      ?? details?.subscriber?.phone?.phone_number?.country_code
      ?? details?.purchase_units?.[0]?.shipping?.phone_number?.country_code
      ?? fallbackUserPhone.phonecode
      ?? '';

    const normalizedPayPalPhone = this.normalizeExternalPhone(paypalPhoneRaw, paypalPhoneCodeRaw);

    // Land muss auf Paradise-Logik gesetzt werden
    const countries = this.baseDataService.getBaseData('countrycodes');
    let countryCodeParadise = null;
    countries.forEach(value => {
      if (value.value == paypalAddress.country) {
        countryCodeParadise = value.key;
      }
    });

    // Paypal kennt keine Anrede, ist aber Pflicht
    const salutation = this.configService.getConfig('paypal.defaultAddressCode');
    let addressCode = null;
    if (salutation) {
      const addressCodes = this.baseDataService.getBaseData('addresscodes');
      addressCodes.forEach(value => {
        if (value.key == salutation.value) {
          addressCode = (value as AddressCode)
        }
      })

    }

    return {
      showAddress: true,
      person: {
        salutation: addressCode ?? '',
        title: '',
        firstname: firstName,
        lastname: lastName,
        initials: '',
        middlename: ''
      },
      userData: {
        email: email,
        phonecode: normalizedPayPalPhone.phonecode,
        phone: normalizedPayPalPhone.phone
      },
      address: {
        street: paypalAddress.street ?? '',
        houseno: paypalAddress.houseno ?? '',
        zipcode: paypalAddress.zipcode ?? '',
        city: paypalAddress.city ?? '',
        country: countryCodeParadise ?? '',
        extraLine: '',
        district: paypalAddress.district ?? ''
      }
    } as AddressFormValue;
  }

  goToBuckaroo(): void {
    this.redirectService.redirect(this.transaction.redirectUrl, true, false);
  }

  private toPayPalStartTime(date: Date | null | undefined): string | null {
    if (!date) {
      return null;
    }
    const requestedDate = new Date(date);
    if (isNaN(requestedDate.getTime())) {
      return null;
    }

    // Bei "heute" kein start_time senden -> PayPal startet das Abo sofort.
    if (this.isToday(requestedDate)) {
      return null;
    }

    // Zukünftiges Datum: den vom Kunden gewählten Kalendertag (lokale Datumskomponenten)
    // fest als 02:00 UTC kodieren. So bleibt die angezeigte Datumskomponente derselbe Tag
    // -> PayPal zeigt genau den gewählten Tag, keine Verschiebung auf den Vortag durch
    // toISOString().
    const startTime = new Date(Date.UTC(
      requestedDate.getFullYear(),
      requestedDate.getMonth(),
      requestedDate.getDate(),
      2, 0, 0, 0
    ));
    return startTime.toISOString();
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();
  }
}
